'use client';

import { Drawer, Heading, Icon, TextPassage } from '@petsmart-ui/sparky';
import cn from 'classnames';
import { useEffect, useId, useState } from 'react';
import { useShallow } from 'zustand/shallow';

import { OptimizelyShipMeter, useCustomerAutoshipSubscriptions, useDecisions } from '@libs/common-next';

import { CartItems } from './CartItems';
import { Footer } from './Footer';
import { useCartConfirmDrawer } from './model/useCartConfirmDrawer';
import './CartConfirmDrawer.scss';

type CartConfirmDrawerProps = {
    /** Hide the Sparky overlay when this drawer is stacked over another drawer. */
    stacked?: boolean;
    /** When the host drawer is closed, the stacked cart confirm drawer should close too. */
    forceClosed?: boolean;
    /** Sparky close (X / overlay / Esc). Used when stacked over another drawer. */
    onCloseAll?: () => void;
};

export const CartConfirmDrawer = ({
    stacked = false,
    forceClosed = false,
    onCloseAll,
}: CartConfirmDrawerProps = {}) => {
    const { autoshipData, isReady: isAutoshipCustomerStatusReady } = useCustomerAutoshipSubscriptions();
    const hasFreeShipMeter = useDecisions('x_pop_sla');
    const { isOpen, isStacked, close, content, items } = useCartConfirmDrawer(
        useShallow(store => ({
            isOpen: store.isOpen,
            isStacked: store.stacked,
            close: store.close,
            content: store.content,
            items: store.items,
        }))
    );
    const headingId = useId();

    // Don't render drawer on server or first client render to prevent Sparky animation issues
    const [hasBeenRendered, setHasBeenRendered] = useState(false);

    useEffect(() => {
        setHasBeenRendered(true);
    }, []);

    useEffect(() => {
        if (forceClosed && stacked && isStacked) {
            close();
        }
    }, [forceClosed, stacked, isStacked, close]);

    const autoshipItem = items.find(
        item => item.autoshipFrequency != null && item.autoshipFrequencyUnitOfMeasure != null
    );
    const isExistingAutoshipCustomer = Boolean(autoshipData?.data?.hasAutoship);

    if (!hasBeenRendered) {
        return null;
    }

    const handleClose = () => {
        close();
        onCloseAll?.();
    };

    const isActive = stacked ? isOpen && isStacked : isOpen && !isStacked;

    return (
        <Drawer
            align="right"
            isActive={isActive}
            onClose={handleClose}
            data-testid="added-to-cart-drawer"
            className={cn('added-to-cart-drawer', { 'added-to-cart-drawer--stacked': stacked })}
            ariaLabelledBy={headingId}
            dismissible
        >
            <Drawer.Header>
                <Icon name="check-circle-fill" />
                <Heading id={headingId} tagName="h2" size="headline">
                    {content.title ?? 'Added to cart'}
                </Heading>
            </Drawer.Header>
            <Drawer.Body>
                {content.subtitle && (
                    <TextPassage size="lg" className="added-to-cart-drawer__subtitle">
                        {content.subtitle}
                    </TextPassage>
                )}
                <CartItems />
                {isActive &&
                    hasFreeShipMeter &&
                    autoshipItem &&
                    isAutoshipCustomerStatusReady &&
                    !isExistingAutoshipCustomer && (
                        <OptimizelyShipMeter
                            autoshipFrequency={Number(autoshipItem.autoshipFrequency)}
                            autoshipFrequencyUnitOfMeasure={Number(autoshipItem.autoshipFrequencyUnitOfMeasure)}
                        />
                    )}
            </Drawer.Body>
            <Drawer.Footer>
                <Footer onCloseAll={onCloseAll} />
            </Drawer.Footer>
        </Drawer>
    );
};
